import { capture } from ".";

interface CalendlyMessageData {
  event: string;
  payload?: unknown;
}

function isCalendlyMessage(data: unknown): data is CalendlyMessageData {
  return (
    typeof data === "object" &&
    data !== null &&
    typeof (data as { event?: unknown }).event === "string" &&
    (data as { event: string }).event.startsWith("calendly.")
  );
}

/**
 * Listens for Calendly's `postMessage` events on `window` and maps them to
 * the conversion funnel in SPEC.md §3.4 (widget opened → date/time selected
 * → demo booked). Returns a cleanup function that removes the listener.
 */
export function attachCalendlyTracking(opts?: {
  mediaId?: string;
  /**
   * Called on `event_scheduled` with the booked invitee's API URI (or null when
   * the untyped message omits it). Lets the page fetch the booker's contact
   * details without adding a second `message` listener.
   */
  onEventScheduled?: (payload: { inviteeUri: string | null }) => void;
}): () => void {
  function commonProps(): Record<string, unknown> {
    return {
      media_id: opts?.mediaId,
      page_path: window.location.pathname,
    };
  }

  /**
   * `calendly_widget_opened` means "the booking UI became visible", and which
   * message signals that depends on the embedded URL. An event-type page
   * (`/robinlim/30min`) sends `event_type_viewed` on load; a profile page
   * (`/robinlim`, the main-site dialog) sends `profile_page_viewed` and only
   * emits `event_type_viewed` once the visitor picks an event type. Listening
   * for one and not the other silently zeroes the funnel on half the site.
   *
   * Both map to the same event, deduped: a visitor who lands on the profile
   * page and then picks an event type triggers both messages.
   */
  let widgetOpenedReported = false;

  function onMessage(e: MessageEvent): void {
    if (!isCalendlyMessage(e.data)) return;

    switch (e.data.event) {
      case "calendly.profile_page_viewed":
      case "calendly.event_type_viewed":
        if (widgetOpenedReported) break;
        widgetOpenedReported = true;
        capture("calendly_widget_opened", commonProps());
        break;
      case "calendly.date_and_time_selected":
        capture("calendly_date_time_selected", commonProps());
        break;
      case "calendly.event_scheduled": {
        capture("demo_booked", commonProps());
        // The message payload is untyped and forgeable — guard the shape before
        // handing the URI on; the receiver re-validates it server-side.
        const payload = e.data.payload as { invitee?: { uri?: unknown } } | undefined;
        const uri = payload?.invitee?.uri;
        opts?.onEventScheduled?.({ inviteeUri: typeof uri === "string" ? uri : null });
        break;
      }
      default:
        break;
    }
  }

  window.addEventListener("message", onMessage);

  return function cleanup(): void {
    window.removeEventListener("message", onMessage);
  };
}
